import { setOrderPartRejectedByClient } from '$api/evoAPIs'
import { PartNoBg } from '$pages/serviceuser/Marketplace/Bookservice/Defaultwizard/styledComponents'
import { StyledSubTable } from './styledComponents'
import { DeleteOutlined } from '@ant-design/icons'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { Popconfirm, Tag, Typography } from 'antd'
import Tooltip from '$components/Tooltip'
import { useTranslation } from 'react-i18next'
import {
  calculateOrderPartUnitPriceRange,
  calculatePricePerUnit,
  calculateOrderPartPriceRange,
  summarizeDeliveryTimes,
} from '../utils'
import OrderPartDetailView from './OrderPartDetailView'
import { StyledLink } from './styledComponents'
import { currencyRenderer, getColorLabel } from '$utils/i18n'
import TruncateText from '$components/TruncateText'
import dayjs from 'dayjs'
import { StyledTable } from '$pages/serviceprovider/ReceivedRequests/CreateOffersView/styledComponents'
import PropTypes from 'prop-types'

const OrderedPartsTable = ({
  orderPartsList,
  orderPartFormData,
  resetOrderPartFormData,
  orderPartListStatus,
  loading,
  editable = false,
  showProviderOffers,
  showRejectedProviderOffers,
  showOfferTerms,
  additionalColumns = [],
}) => {
  const { t } = useTranslation()
  const { Text } = Typography
  const queryClient = useQueryClient()

  if (!orderPartsList.provider_accepted_offer) {
    editable = false
  }

  // once client has accepted offer, rejected parts of offer should
  // - still be rendered
  // - be visually recognizable as rejected
  // - be excluded from price calculations etc.
  const disableRejectedProviderOffers =
    orderPartsList.client_accepted_offer && showRejectedProviderOffers

  const allowOrderPartReject =
    orderPartsList.list_of_orderparts.filter(
      (orderPart) => !orderPart.rejected_by_client,
    ).length > 1

  const validOrderParts = showRejectedProviderOffers
    ? orderPartsList.list_of_orderparts
    : orderPartsList.list_of_orderparts.filter(
        (orderPart) => !orderPart.rejected_by_client,
      )

  const rejectOfferMutation = useMutation({
    mutationFn: setOrderPartRejectedByClient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allorderedpartslists'] })
    },
  })

  const handleDelete = (orderPartId) => {
    if (allowOrderPartReject) {
      rejectOfferMutation.mutate({
        order_part_id: orderPartId,
        rejected_by_client: true,
      })
    }
  }

  const defaultColumns = [
    // this is for space
    ...(!showOfferTerms
      ? [
          {
            title: '',
          },
        ]
      : []),
    {
      title: t('Part No'),
      width: '160px',
      render: (record) => (
        <PartNoBg
          disabled={disableRejectedProviderOffers && record.rejected_by_client}
        >
          {record.part.id_part_client}
        </PartNoBg>
      ),
    },
    {
      title: t('Part Name'),
      render: (record) => {
        const partName = record.part.name || t('unknown')
        return (
          <TruncateText
            text={partName}
            useLineClamp={true}
            style={
              record.status_info == 'discarded'
                ? { textDecoration: 'line-through' }
                : {}
            }
            addedTooltip={
              record.status_info == 'discarded'
                ? t('service.order_part.tooltip.offer_discarded')
                : ''
            }
          />
        )
      },
      ellipsis: true,
    },
    {
      title: t('Amount'),
      dataIndex: 'amount',
    },
    {
      title: t('additive_material_column'),
      render: (record) => {
        const materialName =
          record.operator_additive_material?.material_name || t('unknown')

        const tooltipText = !record.is_oam_changed
          ? t('additive_material_column_tooltip')
          : ''

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              overflow: 'hidden',
              minWidth: 0,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <TruncateText
                text={`${materialName}${getColorLabel(record.selected_color, t)}`}
                addedTooltip={tooltipText}
              />
            </div>
            {record.is_oam_changed && (
              <Tooltip
                title={t(
                  'material_changed_by_operator',
                  'Material changed by operator',
                )}
              >
                <Tag color="orange" style={{ margin: 0 }}>
                  v2
                </Tag>
              </Tooltip>
            )}
          </div>
        )
      },
      ellipsis: true,
    },
    {
      title: t('my_orders.delivery_time'),
      render: (record) => {
        if (!showOfferTerms) {
          return
        }

        const selectedOffers =
          record.provider_offers?.filter((offer) => offer.selected === true) ||
          []
        if (selectedOffers.length > 0) {
          const deliveryDays = selectedOffers
            .map((offer) => offer.delivery_days)
            .filter((days) => days !== null && days !== undefined)

          if (deliveryDays.length > 0) {
            const maxDeliveryDays = Math.max(...deliveryDays)
            return maxDeliveryDays + ' ' + t('days')
          }
        }

        let deliveryTimes = []
        if (editable && orderPartFormData[record.id]) {
          deliveryTimes = Object.values(
            orderPartFormData[record.id].provider_offers || {},
          )
            .map((offer) => offer.delivery_days || offer.base_production_time)
            .filter((days) => days)
        } else {
          deliveryTimes =
            record.provider_offers
              ?.map((offer) =>
                orderPartsList.client_accepted_offer
                  ? offer.delivery_days
                  : [
                      offer.base_production_time,
                      ...offer.production_time_rules.map(
                        (rule) => rule.max_days,
                      ),
                    ],
              )
              .flat() || []
        }
        return t(
          'received_requests.days',
          summarizeDeliveryTimes(deliveryTimes),
        )
      },
    },
    {
      title: t('Price per unit'),
      render: (record) => {
        if (!showOfferTerms) {
          return
        }

        const selectedOffers =
          record.provider_offers?.filter((offer) => offer.selected === true) ||
          []

        if (selectedOffers.length == 1) {
          return currencyRenderer(t)(selectedOffers[0].base_price)
        } else if (selectedOffers.length > 1) {
          const { min, max } = calculateOrderPartUnitPriceRange(selectedOffers)
          return min === max
            ? currencyRenderer(t)(min)
            : `${currencyRenderer(t)(min)} – ${currencyRenderer(t)(max)}`
        }

        let minPricePerUnit
        let maxPricePerUnit
        let pricesPerUnit = []
        if (editable) {
          const providerOffers = Object.values(
            record.status_info !== 'discarded' &&
              orderPartFormData[record.id].provider_offers,
          )
          pricesPerUnit = providerOffers.map((offer) =>
            calculatePricePerUnit(offer),
          )
        } else {
          pricesPerUnit =
            record.status_info !== 'discarded' &&
            record.provider_offers.map((offer) =>
              calculatePricePerUnit(
                offer,
                orderPartsList.client_accepted_offer,
              ),
            )
        }

        if (pricesPerUnit.length > 0) {
          minPricePerUnit = Math.min(...pricesPerUnit)
          maxPricePerUnit = Math.max(...pricesPerUnit)
        }

        return minPricePerUnit === maxPricePerUnit
          ? currencyRenderer(t)(minPricePerUnit)
          : `${currencyRenderer(t)(minPricePerUnit)} – ${currencyRenderer(t)(maxPricePerUnit)}`
      },
    },
    {
      title: t('my_orders.price_total'),
      render: (record) => {
        if (!showOfferTerms) {
          return
        }

        if (record.status_info === 'discarded') {
          return
        }

        // After client accepted offer — show exact price from selected offers
        if (orderPartsList.client_accepted_offer) {
          const selectedOffers =
            record.provider_offers?.filter(
              (offer) => offer.selected === true,
            ) || []

          if (selectedOffers.length > 0) {
            const totalPrice = selectedOffers.reduce(
              (sum, offer) => sum + offer.base_price * offer.amount,
              0,
            )
            return currencyRenderer(t)(totalPrice)
          }
        }

        // Before acceptance — show price range across all provider offers for this part
        let providerOffers
        if (editable && orderPartFormData[record.id]) {
          providerOffers = Object.values(
            orderPartFormData[record.id].provider_offers || {},
          )
        } else {
          providerOffers = record.provider_offers || []
        }

        const { min, max } = calculateOrderPartPriceRange(
          providerOffers,
          orderPartsList.client_accepted_offer,
        )

        return min === max
          ? currencyRenderer(t)(min)
          : `${currencyRenderer(t)(min)} – ${currencyRenderer(t)(max)}`
      },
    },
    ...(editable
      ? [
          {
            title: t('Status'),
            render: (record) => {
              if (
                orderPartListStatus?.[orderPartsList.id]?.includes(record.id)
              ) {
                return (
                  <Tooltip title={t('service.order_part.tooltip.reviewed')}>
                    <Tag color="success">
                      <TruncateText
                        text={t('service.order_part.status.reviewed')}
                      />
                    </Tag>
                  </Tooltip>
                )
              } else {
                return (
                  <Tooltip title={t('service.order_part.tooltip.review')}>
                    <Tag>
                      <TruncateText
                        text={t('service.order_part.status.review')}
                      />
                    </Tag>
                  </Tooltip>
                )
              }
            },
          },
          {
            title: '',
            render: (record) =>
              allowOrderPartReject && (
                <Popconfirm
                  overlayStyle={{
                    width: '270px',
                  }}
                  title={t('service.order_part.confirm_delete')}
                  onConfirm={() => handleDelete(record.id)}
                >
                  <StyledLink>
                    <DeleteOutlined />
                  </StyledLink>
                </Popconfirm>
              ),
          },
        ]
      : []),
  ]

  const expandedRowRender = (orderPart) => {
    let relevantSubOrders

    if (orderPartsList?.list_of_suborders?.length) {
      relevantSubOrders = orderPartsList?.list_of_suborders
        .map((subOrder, index) => ({
          key: index,
          quantity: subOrder.orderpart_quantity?.[orderPart.id],
          desired_delivery_date: subOrder.desired_delivery_date,
        }))
        .filter((row) => row.quantity != null)
    } else {
      // no suborders — fall back to main order
      relevantSubOrders = [
        {
          key: 0,
          quantity: orderPart.amount,
          desired_delivery_date:
            orderPartsList?.main_order?.desired_delivery_date,
        },
      ]
    }

    const subOrderColumns = [
      {
        title: t('operator_orders.batch_number'),
        width: 120,
        render: (_, __, index) => (
          <Text style={{ fontSize: '12px' }}>{index + 1}</Text>
        ),
      },
      {
        title: t('received_requests.amount'),
        dataIndex: 'quantity',
        width: 100,
        render: (val) => <Text style={{ fontSize: '12px' }}>{val}</Text>,
      },
      {
        title: t('received_requests.desired_delivery_date'),
        dataIndex: 'desired_delivery_date',
        render: (val) =>
          val ? (
            <Text style={{ fontSize: '12px' }}>
              {dayjs(val).format('DD/MM/YYYY')}
            </Text>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
    ]

    return (
      <StyledTable
        columns={subOrderColumns}
        dataSource={relevantSubOrders}
        pagination={false}
        size="small"
        style={{ margin: '0 0 8px 8px' }}
      />
    )
  }

  return (
    <StyledSubTable
      size="middle"
      columns={[...defaultColumns, ...additionalColumns]}
      style={{ width: '100%' }}
      dataSource={validOrderParts}
      loading={loading}
      rowKey={(record) => record.id}
      rowClassName={(record) =>
        disableRejectedProviderOffers && record.rejected_by_client
          ? 'row-disabled'
          : undefined
      }
      expandable={
        showProviderOffers
          ? {
              showExpandColumn: true,
              expandRowByClick: true,
              expandedRowClassName: (record) =>
                disableRejectedProviderOffers && record.rejected_by_client
                  ? 'expanded-row-disabled'
                  : undefined,
              expandedRowRender: (record) => (
                <OrderPartDetailView
                  orderPartListId={orderPartsList.id}
                  clientAcceptedOffer={orderPartsList.client_accepted_offer}
                  orderPart={record}
                  resetFormData={() => resetOrderPartFormData(record.id)}
                  editable={editable}
                />
              ),
            }
          : {
              expandedRowRender,
              rowExpandable: () => true,
              expandRowByClick: true,
            }
      }
    />
  )
}

OrderedPartsTable.propTypes = {
  orderPartsList: PropTypes.shape({
    client_accepted_offer: PropTypes.bool,
    main_order: PropTypes.shape({
      desired_delivery_date: PropTypes.string,
      allow_partial_delivery: PropTypes.bool,
      allow_other_suppliers: PropTypes.bool,
      allow_subcontractors: PropTypes.bool,
    }).isRequired,
    list_of_suborders: PropTypes.array,
  }),
}

export default OrderedPartsTable
